import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { isNational } from "@/lib/roles";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/context/I18nContext";
import { confirmWithUndo } from "@/lib/undo";
import { Plus, Download, Search, Archive, Pencil, UserPlus, EyeOff, Eye } from "lucide-react";

const SECTIONS = ["Cubs", "Scouts", "Senior Scouts", "Rovers"];
const POSITIONS = [
  "Member",
  "Patrol Co-Leader",
  "Patrol Leader",
  "Cubs Leader",
  "Scout Leader",
  "Chapter Leader",
  "Regional Admin",
];

const ROLE_LABEL = {
  scout: "Scout", parent: "Parent",
  patrol_co_leader: "Patrol Co-Leader", patrol_leader: "Patrol Leader",
  cubs_leader: "Cubs Leader", scout_leader: "Scout Leader",
  chapter_leader: "Chapter Leader", regional_admin: "Regional Admin",
  national_admin: "National Admin",
};

const emptyForm = {
  full_name: "", full_name_hy: "", email: "", phone: "", dob: "", gender: "",
  chapter_id: "", section: "Scouts", patrol: "",
  guardian_name: "", guardian_phone: "", emergency_contact: "",
  membership_start: "", position: "Member", rank: "", degree: "", notes: "", status: "active",
};

const DEFAULT_VOCAB = {
  ranks: ["Cub", "Scout", "Senior Scout", "Rover", "Guide"],
  positions: ["Member", "Patrol Co-Leader", "Patrol Leader", "Cubs Leader", "Scout Leader", "Chapter Leader", "Regional Admin"],
  patrols: ["Mrjuyn", "Aryudz", "Vagr", "Arjouyn", "Hovaz", "Shoonch"],
  degrees: ["First Degree", "Second Degree", "Third Degree", "Honor Scout"],
};

export default function Members() {
  const { user } = useAuth();
  const { t } = useT();
  const [members, setMembers] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [q, setQ] = useState("");
  const [section, setSection] = useState("all");
  const [status, setStatus] = useState("all");
  const [chapter, setChapter] = useState("all");
  const [hideArchived, setHideArchived] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // member being edited
  const [form, setForm] = useState({ ...emptyForm, chapter_id: user?.chapter_id || "" });
  const [selected, setSelected] = useState(new Set());
  const [vocab, setVocab] = useState(DEFAULT_VOCAB);

  useEffect(() => {
    api.get("/vocab")
      .then(r => setVocab({ ...DEFAULT_VOCAB, ...r.data }))
      .catch(err => console.warn("vocab load failed:", err?.message));
    const onVocab = () => api.get("/vocab").then(r => setVocab({ ...DEFAULT_VOCAB, ...r.data })).catch(() => {});
    window.addEventListener("vocab-updated", onVocab);
    return () => window.removeEventListener("vocab-updated", onVocab);
  }, []);

  const load = async () => {
    const params = new URLSearchParams();
    if (chapter !== "all") params.append("chapter_id", chapter);
    if (section !== "all") params.append("section", section);
    if (status !== "all") params.append("status", status);
    if (q) params.append("q", q);
    // ask backend to include archived when the user wants to see them (toggle off = show)
    if (status === "all" && !hideArchived) params.append("include_archived", "true");
    const { data } = await api.get(`/members?${params}`);
    const filtered = hideArchived && status === "all"
      ? data.filter(m => m.status !== "archived")
      : data;
    setMembers(filtered); setSelected(new Set());
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, section, status, chapter, hideArchived]);
  useEffect(() => { api.get("/chapters").then(r => setChapters(r.data)); }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ ...emptyForm, chapter_id: user?.chapter_id || "" });
    setOpen(true);
  };
  const openEdit = (m) => {
    setEditing(m);
    setForm({ ...emptyForm, ...m });
    setOpen(true);
  };
  const save = async () => {
    try {
      const payload = {
        ...form,
        chapter_id: form.chapter_id || user?.chapter_id,
        rank: form.rank === "__none__" ? "" : (form.rank || ""),
        degree: form.degree === "__none__" ? "" : (form.degree || ""),
      };
      if (editing) await api.put(`/members/${editing.member_id}`, payload);
      else await api.post("/members", payload);
      toast.success(editing ? "Member updated" : "Member added");

      // Auto-sync user role prompt when a leadership position is set
      if (editing && ["Patrol Co-Leader","Patrol Leader","Cubs Leader","Scout Leader","Chapter Leader","Regional Admin"].includes(payload.position)) {
        try {
          const { data } = await api.post(`/members/${editing.member_id}/sync-user-role`, { apply: false });
          if (data.linked && data.needs_change) {
            const label = ROLE_LABEL[data.suggested_role] || data.suggested_role;
            toast(`Upgrade ${payload.full_name}'s account role?`, {
              description: `${ROLE_LABEL[data.current_role] || data.current_role} → ${label}`,
              duration: 8000,
              action: {
                label: "Apply",
                onClick: async () => {
                  try {
                    await api.post(`/members/${editing.member_id}/sync-user-role`, { apply: true });
                    toast.success(`Role upgraded to ${label}`);
                  } catch { toast.error("Failed to upgrade role"); }
                },
              },
            });
          }
        } catch (err) { console.warn("role-sync check failed:", err?.message); }
      }
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const [parentDialog, setParentDialog] = useState(null); // member being invited
  const [parentEmail, setParentEmail] = useState("");
  const [parentName, setParentName] = useState("");
  const inviteParent = async () => {
    if (!parentDialog) return;
    try {
      const { data } = await api.post(`/members/${parentDialog.member_id}/invite-parent`, {
        email: parentEmail, name: parentName,
      });
      if (data.temp_password) {
        toast.success(`Parent invited`, { description: `Temp password: ${data.temp_password}`, duration: 15000 });
      } else {
        toast.success("Parent linked to existing account");
      }
      setParentDialog(null); setParentEmail(""); setParentName("");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const archiveOne = (m) => {
    const prev = members;
    setMembers(members.filter(x => x.member_id !== m.member_id));
    confirmWithUndo({
      message: `Archived ${m.full_name}`,
      doDelete: async () => { await api.delete(`/members/${m.member_id}`); },
      restore: () => setMembers(prev),
    });
  };
  const archiveBulk = () => {
    if (!selected.size) return;
    const ids = Array.from(selected);
    const prev = members;
    const kept = members.filter(m => !selected.has(m.member_id));
    setMembers(kept); setSelected(new Set());
    confirmWithUndo({
      message: `Archived ${ids.length} members`,
      doDelete: async () => { for (const id of ids) await api.delete(`/members/${id}`); },
      restore: () => setMembers(prev),
    });
  };

  const toggleAll = (checked) => {
    if (checked) setSelected(new Set(members.map(m => m.member_id)));
    else setSelected(new Set());
  };
  const toggleOne = (id, checked) => {
    const n = new Set(selected);
    if (checked) n.add(id); else n.delete(id);
    setSelected(n);
  };

  const exportExcel = async () => {
    const XLSX = await import("xlsx");
    const headers = ["full_name", "full_name_hy", "email", "phone", "gender", "section", "patrol", "chapter_id", "status", "position", "membership_start"];
    const rows = members.map(m => Object.fromEntries(headers.map(h => [h, m[h] ?? ""])));
    const ws = XLSX.utils.json_to_sheet(rows, { header: headers });
    // widen columns
    ws["!cols"] = headers.map(h => ({ wch: Math.max(14, h.length + 2) }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Members");
    const date = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `members-${date}.xlsx`);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="uppercase-label">{t("Database", "Տվյալների բազա")}</div>
          <h1 className="font-display text-4xl lg:text-5xl font-black tracking-tight mt-1">{t("Members", "Անդամներ")}</h1>
          <p className="text-muted-foreground mt-1 text-sm">{members.length} {t("shown", "ցուցադրված է")}{selected.size ? ` · ${selected.size} ${t("selected", "ընտրված")}` : ""}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            className="btn-pill"
            onClick={() => setHideArchived(!hideArchived)}
            data-testid="toggle-hide-archived"
          >
            {hideArchived ? <><EyeOff size={14} className="mr-2"/>Archived hidden</> : <><Eye size={14} className="mr-2"/>Showing archived</>}
          </Button>
          {selected.size > 0 && (
            <Button variant="outline" className="btn-pill text-[hsl(0,65%,55%)] border-[hsl(0,65%,55%)]/40 hover:bg-[hsl(0,65%,55%)]/10" onClick={archiveBulk} data-testid="bulk-archive-members">
              <Archive size={16} className="mr-2"/> Archive selected ({selected.size})
            </Button>
          )}
          <Button variant="outline" className="btn-pill" onClick={exportExcel} data-testid="export-excel"><Download size={16} className="mr-2"/> Excel</Button>
          <Button className="btn-pill bg-[hsl(12,65%,63%)]" onClick={openNew} data-testid="new-member-btn"><Plus size={16} className="mr-2"/>{t("New Member", "Նոր անդամ")}</Button>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? t("Edit Member", "Խմբագրել անդամ") : t("New Member", "Նոր անդամ")}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Full name</Label><Input value={form.full_name} onChange={e => setForm({...form, full_name: e.target.value})} data-testid="mbr-name"/></div>
            <div><Label>Full name (Armenian)</Label><Input value={form.full_name_hy || ""} onChange={e => setForm({...form, full_name_hy: e.target.value})}/></div>
            <div><Label>Email</Label><Input value={form.email || ""} onChange={e => setForm({...form, email: e.target.value})}/></div>
            <div><Label>Phone</Label><Input value={form.phone || ""} onChange={e => setForm({...form, phone: e.target.value})}/></div>
            <div><Label>Date of birth</Label><Input type="date" value={form.dob || ""} onChange={e => setForm({...form, dob: e.target.value})}/></div>
            <div><Label>Gender</Label>
              <Select value={form.gender || ""} onValueChange={v => setForm({...form, gender: v})}>
                <SelectTrigger data-testid="member-gender"><SelectValue placeholder="Select gender"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Male">Male</SelectItem>
                  <SelectItem value="Female">Female</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {isNational(user) && (
              <div><Label>Chapter</Label>
                <Select value={form.chapter_id || ""} onValueChange={v => setForm({...form, chapter_id: v})}>
                  <SelectTrigger><SelectValue placeholder="Choose"/></SelectTrigger>
                  <SelectContent>{chapters.map(c => <SelectItem key={c.chapter_id} value={c.chapter_id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div><Label>Section</Label>
              <Select value={form.section} onValueChange={v => setForm({...form, section: v})}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent>{SECTIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>{t("Patrol", "Պարեկ")}</Label>
              <Select value={form.patrol || ""} onValueChange={v => setForm({...form, patrol: v})}>
                <SelectTrigger data-testid="member-patrol"><SelectValue placeholder={t("Select patrol", "Ընտրել պարեկ")}/></SelectTrigger>
                <SelectContent>
                  {(vocab.patrols || []).map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>{t("Rank", "Աստիճան")}</Label>
              <Select value={form.rank || ""} onValueChange={v => setForm({...form, rank: v})}>
                <SelectTrigger data-testid="mbr-rank"><SelectValue placeholder={t("Select rank", "Ընտրել աստիճան")}/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">{t("— None —", "— Չկա —")}</SelectItem>
                  {(vocab.ranks || []).map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>{t("Leadership position", "Ղեկավար պաշտոն")}</Label>
              <Select value={form.position || "Member"} onValueChange={v => setForm({...form, position: v})}>
                <SelectTrigger data-testid="mbr-position"><SelectValue/></SelectTrigger>
                <SelectContent>{(vocab.positions || []).map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>{t("Scout degree", "Սկաուտական աստիճան")}</Label>
              <Select value={form.degree || ""} onValueChange={v => setForm({...form, degree: v})}>
                <SelectTrigger data-testid="mbr-degree"><SelectValue placeholder={t("Select degree", "Ընտրել")}/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">{t("— None —", "— Չկա —")}</SelectItem>
                  {(vocab.degrees || []).map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Status</Label>
              <Select value={form.status || "active"} onValueChange={v => setForm({...form, status: v})}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Guardian name</Label><Input value={form.guardian_name || ""} onChange={e => setForm({...form, guardian_name: e.target.value})}/></div>
            <div><Label>Guardian phone</Label><Input value={form.guardian_phone || ""} onChange={e => setForm({...form, guardian_phone: e.target.value})}/></div>
            <div><Label>Emergency contact</Label><Input value={form.emergency_contact || ""} onChange={e => setForm({...form, emergency_contact: e.target.value})}/></div>
            <div><Label>Membership start</Label><Input type="date" value={form.membership_start || ""} onChange={e => setForm({...form, membership_start: e.target.value})}/></div>
          </div>
          <Button onClick={save} className="btn-pill w-full bg-[hsl(149,40%,30%)] mt-2" data-testid="mbr-save">
            {editing ? "Save changes" : "Add member"}
          </Button>
          {editing && form.position && ["Chapter Leader", "Regional Admin"].includes(form.position) && (
            <p className="text-xs text-muted-foreground mt-2">
              Tip: to give this member sign-in permissions matching their new role, also update their user account in <Link to="/administration" className="text-[hsl(12,65%,63%)] font-semibold">Administration</Link>.
            </p>
          )}
        </DialogContent>
      </Dialog>

      <Card className="clay-card p-4">
        <div className="grid md:grid-cols-4 gap-3">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-3 text-muted-foreground" />
            <Input placeholder="Search name or email" value={q} onChange={e => setQ(e.target.value)} className="pl-9" data-testid="mbr-search"/>
          </div>
          {isNational(user) && (
            <Select value={chapter} onValueChange={setChapter}>
              <SelectTrigger><SelectValue placeholder="Chapter"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All chapters</SelectItem>
                {chapters.map(c => <SelectItem key={c.chapter_id} value={c.chapter_id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Select value={section} onValueChange={setSection}>
            <SelectTrigger><SelectValue placeholder="Section"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sections</SelectItem>
              {SECTIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger><SelectValue placeholder="Status"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
              <SelectItem value="archived">Archived</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      <Card className="clay-card p-0 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <Checkbox
                  checked={selected.size > 0 && selected.size === members.length}
                  onCheckedChange={toggleAll}
                  data-testid="mbr-check-all"
                />
              </TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Section</TableHead>
              <TableHead>Patrol</TableHead>
              <TableHead>Position</TableHead>
              <TableHead>Chapter</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {members.map((m, i) => (
              <TableRow key={m.member_id} className={i % 2 ? "bg-muted/30" : ""} data-testid={`member-row-${m.member_id}`}>
                <TableCell>
                  <Checkbox
                    checked={selected.has(m.member_id)}
                    onCheckedChange={(v) => toggleOne(m.member_id, v)}
                    data-testid={`mbr-check-${m.member_id}`}
                  />
                </TableCell>
                <TableCell>
                  <Link to={`/members/${m.member_id}`} className="flex items-center gap-3 hover:text-[hsl(12,65%,63%)]">
                    <div className="w-9 h-9 rounded-full bg-[hsl(12,65%,63%)]/20 text-[hsl(12,65%,63%)] flex items-center justify-center font-bold">{m.full_name[0]}</div>
                    <div>
                      <div className="font-semibold">{m.full_name}</div>
                      <div className="text-xs text-muted-foreground">{m.full_name_hy}</div>
                    </div>
                  </Link>
                </TableCell>
                <TableCell>{m.section}</TableCell>
                <TableCell>{m.patrol}</TableCell>
                <TableCell>
                  {["Chapter Leader", "Regional Admin", "Patrol Leader"].includes(m.position)
                    ? <Badge className="rounded-full bg-[hsl(149,40%,30%)]">{m.position}</Badge>
                    : <span className="text-sm text-muted-foreground">{m.position}</span>}
                </TableCell>
                <TableCell><span className="text-xs">{chapters.find(c => c.chapter_id === m.chapter_id)?.name || m.chapter_id}</span></TableCell>
                <TableCell><Badge variant={m.status === "active" ? "default" : "secondary"} className="rounded-full">{m.status}</Badge></TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(m)} data-testid={`edit-mbr-${m.member_id}`}>
                    <Pencil size={14}/>
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => { setParentDialog(m); setParentEmail(""); setParentName(""); }} title="Invite parent" data-testid={`invite-parent-${m.member_id}`}>
                    <UserPlus size={14}/>
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => archiveOne(m)} data-testid={`archive-mbr-${m.member_id}`}>
                    <Archive size={14} />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!parentDialog} onOpenChange={(o) => !o && setParentDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Invite Parent</DialogTitle></DialogHeader>
          {parentDialog && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Send a read-only account to a parent so they can follow <b>{parentDialog.full_name}</b>'s progress, attendance and upcoming activities.
              </p>
              <div><Label>Parent email</Label><Input type="email" value={parentEmail} onChange={e => setParentEmail(e.target.value)} data-testid="parent-email-input"/></div>
              <div><Label>Parent name</Label><Input value={parentName} onChange={e => setParentName(e.target.value)} placeholder={parentDialog.guardian_name || ""} data-testid="parent-name-input"/></div>
              <Button onClick={inviteParent} className="btn-pill w-full bg-[hsl(149,40%,30%)]" data-testid="parent-invite-submit">Send invite</Button>
              <p className="text-[10px] text-muted-foreground">If this email already has an account, we'll simply link the child.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
